import { useEffect, useMemo, useRef, type ReactNode } from 'react'
import type { OrderMessageWithUser } from 'src/api/db/apiOrderMessage'
import type { ChatMessage } from 'src/shared/components/SaraChat/types/chatMessage'
import { ThinkingBubble } from 'src/shared/components/SaraChat/message_components/LoadingComponent'
import type { ProductItem } from 'src/shared/components/SaraChat/message_components/ProductSelectionMessage/types'
import { Column } from 'src/shared/components/Semantic/all'
import { ScrollArea } from 'src/shared/components/ui'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { formatMessageDateGroup } from '../utils/chatMessageDisplay'
import { RequestLocalChatNote } from './Chat/components/RequestLocalChatNote'
import { RequestTeamPingMessage } from './Chat/components/RequestTeamPingMessage'
import type { LocalChatNote } from './Chat/types'
import { isTeamPingMessage } from './Chat/utils/teamPingMessage'
import { RequestChatDateDivider } from './RequestChatDateDivider'
import { RequestChatMessage } from './RequestChatMessage'
import {
  isPendingMockupGeneration,
  RequestSaraMessage,
} from './RequestSaraMessage'
import { RequestSaraMessageGroup } from './RequestSaraMessageGroup'

type TimelineItem =
  | { kind: 'sales'; createdAt: string; message: OrderMessageWithUser }
  | { kind: 'team_ping'; createdAt: string; message: OrderMessageWithUser }
  | {
      kind: 'sara'
      createdAt: string
      message: ChatMessage
      isPrivate: boolean
    }
  | {
      kind: 'sara_group'
      createdAt: string
      items: Array<{ message: ChatMessage; isPrivate: boolean }>
    }
  | { kind: 'local'; createdAt: string; note: LocalChatNote }

export const RequestChatView = ({
  messages,
  saraMessages = [],
  localNotes = [],
  isPending,
  isSaraLoading = false,
  quoteId,
  composer,
  pingCta,
  onOpenProductOverlay,
  onSaraQuickReply,
  flushHeader = false,
  emptyCopy,
}: {
  messages: OrderMessageWithUser[]
  /** Sara messages already annotated with privacy (and filtered for the viewer). */
  saraMessages?: Array<{ message: ChatMessage; isPrivate: boolean }>
  localNotes?: LocalChatNote[]
  isPending?: boolean
  isSaraLoading?: boolean
  quoteId?: number
  composer: ReactNode
  pingCta?: ReactNode
  /** When the sidebar header is in-flow (admin tabs), skip overlay padding. */
  flushHeader?: boolean
  emptyCopy?: string
  onOpenProductOverlay?: (
    products: ProductItem[],
    quote_id: number,
    product_categories?: string[]
  ) => void
  onSaraQuickReply?: (phrase: string) => void
}) => {
  const { t } = useLocalization()
  const scrollAreaRef = useRef<HTMLDivElement>(null)

  const groupedItems = useMemo(() => {
    const timeline: TimelineItem[] = [
      ...messages.map((message) =>
        isTeamPingMessage(message)
          ? {
              kind: 'team_ping' as const,
              createdAt: message.created_at,
              message,
            }
          : {
              kind: 'sales' as const,
              createdAt: message.created_at,
              message,
            }
      ),
      ...saraMessages.map(({ message, isPrivate }) => ({
        kind: 'sara' as const,
        createdAt: message.created_at ?? new Date().toISOString(),
        message,
        isPrivate,
      })),
      ...localNotes.map((note) => ({
        kind: 'local' as const,
        createdAt: note.createdAt,
        note,
      })),
    ].sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    )

    // Merge consecutive Sara messages (except her "user" messages, which
    // render as "You") into a single block with one avatar/header.
    const merged: TimelineItem[] = []
    timeline.forEach((item) => {
      if (item.kind === 'sara' && item.message.role !== 'user') {
        const last = merged[merged.length - 1]
        if (last?.kind === 'sara_group') {
          last.items.push({ message: item.message, isPrivate: item.isPrivate })
          return
        }
        merged.push({
          kind: 'sara_group',
          createdAt: item.createdAt,
          items: [{ message: item.message, isPrivate: item.isPrivate }],
        })
        return
      }
      merged.push(item)
    })

    const groups: { dateGroup: string; items: TimelineItem[] }[] = []

    merged.forEach((item) => {
      const dateGroup = formatMessageDateGroup(item.createdAt)
      const lastGroup = groups[groups.length - 1]
      if (lastGroup?.dateGroup === dateGroup) {
        lastGroup.items.push(item)
        return
      }
      groups.push({ dateGroup, items: [item] })
    })

    return groups
  }, [messages, saraMessages, localNotes])

  const lastSaraGroupId = (() => {
    let id: number | null = null
    for (const group of groupedItems) {
      for (const item of group.items) {
        if (item.kind === 'sara_group') {
          id = item.items[0].message.id
        }
      }
    }
    return id
  })()

  const hasTimelineItems = groupedItems.length > 0 || isSaraLoading
  const mockupProgressKey = saraMessages
    .map(({ message }) => {
      if (message.role !== 'system') return ''
      const raw =
        typeof message.content === 'string'
          ? message.content
          : JSON.stringify(message.content ?? '')
      return raw.includes('mockup_generation') ? raw : ''
    })
    .join()
  const hideThinkingBubble = (() => {
    if (saraMessages.some(({ message }) => isPendingMockupGeneration(message))) {
      return true
    }
    const sorted = [...saraMessages].sort(
      (a, b) =>
        new Date(a.message.created_at ?? 0).getTime() -
        new Date(b.message.created_at ?? 0).getTime()
    )
    for (let i = sorted.length - 1; i >= 0; i--) {
      const role = sorted[i].message.role
      if (role === 'user') break
      if (role === 'status' || role === 'system') return true
    }
    return false
  })()

  useEffect(() => {
    const scrollElement = scrollAreaRef.current?.querySelector(
      '[data-radix-scroll-area-viewport]'
    )
    if (scrollElement) {
      scrollElement.scrollTop = scrollElement.scrollHeight
    }
  }, [
    messages.length,
    saraMessages.length,
    mockupProgressKey,
    localNotes.length,
    isPending,
    isSaraLoading,
  ])

  return (
    <Column className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
      <ScrollArea ref={scrollAreaRef} className="min-h-0 flex-1 px-1">
        <Column className={flushHeader ? 'gap-1 pb-3 pt-3' : 'gap-1 pb-3 pt-16'}>
          {isPending ? (
            <Column className="items-center justify-center px-6 py-10">
              <p className="text-sm text-muted-foreground">
                {t('Loading messages...', 'Laddar meddelanden...')}
              </p>
            </Column>
          ) : !hasTimelineItems ? (
            <Column className="items-center gap-2 px-6 py-10 text-center">
              <p className="max-w-[320px] text-sm text-muted-foreground">
                {emptyCopy ??
                  t(
                    'This is a chat between you and the Klayder team, with help from our assistant Sara whenever you want.',
                    'Det här är en chatt mellan dig och Klayder-teamet, med hjälp av vår assistent Sara när du vill.'
                  )}
              </p>
            </Column>
          ) : (
            <>
              {groupedItems.map((group) => (
                <Column key={group.dateGroup} className="gap-1">
                  <RequestChatDateDivider label={group.dateGroup} />
                  {group.items.map((item) => {
                    if (item.kind === 'team_ping') {
                      return (
                        <RequestTeamPingMessage
                          key={`team-ping-${item.message.id}`}
                          message={item.message}
                        />
                      )
                    }

                    if (item.kind === 'sales') {
                      return (
                        <RequestChatMessage
                          key={`sales-${item.message.id}`}
                          message={item.message}
                        />
                      )
                    }

                    if (item.kind === 'sara') {
                      if (quoteId == null) return null
                      return (
                        <RequestSaraMessage
                          key={`sara-${item.message.id}`}
                          message={item.message}
                          quoteId={quoteId}
                          isPrivate={item.isPrivate}
                          onOpenProductOverlay={onOpenProductOverlay}
                        />
                      )
                    }

                    if (item.kind === 'sara_group') {
                      if (quoteId == null) return null
                      return (
                        <RequestSaraMessageGroup
                          key={`sara-group-${item.items[0].message.id}`}
                          items={item.items}
                          quoteId={quoteId}
                          onOpenProductOverlay={onOpenProductOverlay}
                          onQuickReply={
                            !isSaraLoading &&
                            onSaraQuickReply &&
                            item.items[0].message.id === lastSaraGroupId
                              ? onSaraQuickReply
                              : undefined
                          }
                        />
                      )
                    }

                    return (
                      <RequestLocalChatNote
                        key={`local-${item.note.id}`}
                        note={item.note}
                      />
                    )
                  })}
                </Column>
              ))}

              {isSaraLoading && !hideThinkingBubble && (
                <Column className="px-4 py-2">
                  <ThinkingBubble />
                </Column>
              )}
            </>
          )}
        </Column>
      </ScrollArea>

      <Column className="shrink-0 bg-gradient-to-t from-white via-white to-white/80">
        {pingCta}
        {composer}
      </Column>
    </Column>
  )
}
